import React, { useState, useEffect } from "react";
import ReactSelect from "react-select";
import { useParams, useNavigate } from "react-router-dom";
import {
  getRefundVoucherById,
  getRelatedRefundVouchers,
} from "../../data/mockRefundVouchers";
import ApprovalActions from "./ApprovalActions";
import RelatedVouchers from "./RelatedVouchers";
import Breadcrumb from "../common/Breadcrumb";

import "./RefundVoucherDetail.css";
import { CheckCircle2 } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  TextField,
  Typography,
  Box,
  Button,
  Chip,
  FormControl,
  Select,
  MenuItem,
  Grid,
  InputLabel,
  Card,
  CardContent,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Dialog,
  DialogTitle,
  DialogContentText,
  DialogActions,
  CircularProgress,
  IconButton,
  Divider,
  InputAdornment,
} from "@mui/material";
import { RemoveCircleOutline, AddCircleOutline } from "@mui/icons-material";
import { useGetDetail, useGetList, usePost } from "../../hooks";
import formatDate from "../../Utils/formatDate";

const ReFundVoucherDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [voucher, setVoucher] = useState(null);
  const [relatedVouchers, setRelatedVouchers] = useState([]);

  const { data, loading, error, fetchData } = useGetDetail(
    "refund/treatment-voucher",
    id,
  );
  const { data: treatmentsData, loading: treatmentsLoading } = useGetList(
    "v1/treatments/lists",
  );

  const { postData, loading: postLoading } = usePost(
    `refund/treatment-voucher/${id}`,
  );

  const [approveModal, setApproveModal] = useState({
    open: false,
  });

  // Treatment items state - initialize from API data
  const [treatmentItems, setTreatmentItems] = useState([]);
  const { data: accountingData, loading: accountingLoading } =
    useGetList("accounting-lists");

  // Refund form state
  const [refundMethod, setRefundMethod] = useState("cash");
  const [refundType, setRefundType] = useState("CashBack");
  const [refundDate, setRefundDate] = useState(
    new Date().toISOString().split("T")[0],
  );

  const [account, setAccount] = useState();
  const [filteredAccounts, setFilteredAccount] = useState([]);
  const [remark, setRemark] = useState("");

  // Treatment voucher design state
  const [selectedTreatment, setSelectedTreatment] = useState("");
  const [voucherItems, setVoucherItems] = useState([]);
  const [replacementDiscount, setReplacementDiscount] = useState(0);
  const [cashBackAmount, setCashBackAmount] = useState(0);
  const [refundPaidAmount, setRefundPaidAmount] = useState(0);

  // Calculate total refund amount
  const totalRefundAmount = treatmentItems.reduce((total, item) => {
    if (item.selected && item.refundAmount) {
      return total + parseFloat(item.refundAmount || 0);
    }
    return total;
  }, 0);

  // Handle individual item selection
  const handleItemSelect = (itemId) => {
    setTreatmentItems((prevItems) =>
      prevItems.map((item) => {
        if (item._id === itemId) {
          const newSelected = !item.selected;
          return {
            ...item,
            selected: newSelected,
            refundAmount: newSelected ? item.totalAmount.toString() : "",
          };
        }
        return item;
      }),
    );
  };

  // Handle select all items
  const handleSelectAll = (event) => {
    const checked = event.target.checked;
    setTreatmentItems((prevItems) =>
      prevItems.map((item) => ({
        ...item,
        selected: checked,
        refundAmount: checked ? item.totalAmount.toString() : "",
      })),
    );
  };

  // Handle refund amount change
  const handleRefundAmountChange = (itemId, value) => {
    const numValue = value.replace(/[^0-9.]/g, "");
    const item = treatmentItems.find((item) => item._id === itemId);

    if (parseFloat(numValue) <= item.totalAmount) {
      setTreatmentItems((prevItems) =>
        prevItems.map((item) =>
          item._id === itemId ? { ...item, refundAmount: numValue } : item,
        ),
      );
    }
  };

  // Get selected items count
  const selectedCount = treatmentItems.filter((item) => item.selected).length;
  const isAllSelected =
    selectedCount === treatmentItems.length && treatmentItems.length > 0;
  const isIndeterminate =
    selectedCount > 0 && selectedCount < treatmentItems.length;

  const handleApprove = () => {
    setApproveModal({ open: true });
  };
  const handleReject = () => {};

  // Treatment voucher design handlers
  const handleTreatmentSelect = (treatmentId) => {
    setSelectedTreatment(treatmentId);
    const selectedTreatmentData = treatmentsData?.data?.find(
      (t) => t._id === treatmentId,
    );
    if (selectedTreatmentData) {
      const newItem = {
        _id: selectedTreatmentData?._id,
        item_id: selectedTreatmentData?._id,
        name: selectedTreatmentData?.name,
        qty: 1,
        price: selectedTreatmentData?.sellingPrice || 0,
        total: (selectedTreatmentData?.sellingPrice || 0) * 1,
      };
      setVoucherItems((prev) => [...prev, newItem]);
    }
  };

  const handleRemoveItem = (itemId) => {
    setVoucherItems((prevItems) =>
      prevItems.filter((item) => item._id !== itemId),
    );
  };

  const calculateReplacementTotal = () => {
    return voucherItems.reduce(
      (total, item) => total + item.qty * item.price,
      0,
    );
  };

  const calculateNetTotal = () => {
    const total = calculateReplacementTotal();
    return Math.max(0, total - replacementDiscount);
  };

  const cashAcc = {
    pettyCash: "6470e28964b1ba9496d24a8c",
    tcl: "64e885f55a7d518bb583f929",
    sok: "646747f259a9bc811d98072d",
    mdy: "6467480459a9bc811d980733",
    eightMile: "6470e2aa64b1ba9496d24a92",
    npt: "6467482b59a9bc811d98073f",
    las: "6467481959a9bc811d980739",
    tamwe: "66f3840ae8f3e9fcd3aa1075",
    knas: "6528b9e8b4e1b244376ec8da",
    sanc: "65437c11780be202660ccbad",
    tgg: "65437c33780be202660ccbf9",
    tgy: "65056be28a572e8de464c26f",
    hty: "6646c776cd3587a20dff41cc",
    lmd: "675a6915b2a9c1d91266a15a",
    mlm: "68bea72c31d18e3a4199106b",
  };

  const filterCash = accountingData?.list?.filter((item) =>
    Object.values(cashAcc).includes(item._id),
  );

  const filterBank = accountingData?.list?.filter((item) => {
    return !Object.values(cashAcc).includes(item._id);
  });

  // calculate cashBackAmount

  const confirmApprove = async () => {
    const selectedTreatmentSelections = treatmentItems
      .filter((item) => item.selected && item.isTreatment)
      .map((item) => ({
        _id: item._id,
        refundAmount: item.refundAmount,
      }));

    const selectedDentalTreatmentSelections = treatmentItems
      .filter((item) => item.selected && item.isDentalTreatment)
      .map((item) => ({
        _id: item._id,
        refundAmount: item.refundAmount,
      }));

    const selectedTreatmentPackageSelections = treatmentItems
      .filter((item) => item.selected && item.isPackage)
      .map((item) => ({
        _id: item._id,
        refundAmount: item.refundAmount,
      }));

    const payload = {
      selectedTreatmentPackageSelections,
      selectedDentalTreatmentSelections,
      selectedTreatmentSelections,
      totalRefundAmount: totalRefundAmount,
      relatedAccount: account,
      remark,
      paymentType: refundMethod,
      refundType: refundType,
      relatedBranch: data.relatedBranch,
    };

    if (refundType === "Treatment") {
      payload.multiTreatment = voucherItems;
      payload.refundDiscount = replacementDiscount;
      payload.cashBackAmount = cashBackAmount;
      payload.refundPaidAmount = refundPaidAmount;
    }

    await postData(payload);
    setApproveModal({ open: false });
    navigate("/voucher");
  };
  const closeApproveModal = () => {
    setApproveModal({ open: false });
  };

  const handleAccountChange = (e) => {
    setAccount(e.target.value);
  };

  // Initialize treatment items when data loads
  useEffect(() => {
    if (data?.multiTreatment?.length > 0) {
      const initialItems = data.multiTreatment?.map((item) => {
        return {
          _id: item.relatedTreatmentSelections[0]?._id,
          name: item.item_id?.name,
          qty: item?.qty || 1,
          unitPrice: item.price || 0,
          totalAmount: item?.qty * item.price || 0,
          selected: false,
          isTreatment: true,
          isPackage: false,
          isDentalTreatment: false,
          refundAmount: "",
        };
      });
      setTreatmentItems(initialItems);
    }

    if (data?.relatedTreatmentPackageSelection?.length > 0) {
      const initialItems = data.relatedTreatmentPackageSelection.map((item) => {
        const treatment = item.multiTreatmentPackage.find(
          (el) => el.item_id === item.relatedTreatmentPackage._id,
        );
        return {
          _id: item._id,
          name: item.relatedTreatmentPackage.name,
          qty: treatment?.qty || 1,
          unitPrice: treatment?.price || 0,
          totalAmount: item.totalAmount || 0,
          selected: false,
          isTreatment: false,
          isPackage: true,
          isDentalTreatment: false,
          refundAmount: "",
        };
      });
      setTreatmentItems(initialItems);
    }

    if (data?.multiDentalTreatment?.length > 0) {
      const initialItems = data.multiDentalTreatment?.map((item) => {
        return {
          _id: item?.relatedDentalTreatmentSelections[0]?._id,
          name: item.item_id?.name,
          qty: item?.qty || 1,
          unitPrice: item.price || 0,
          totalAmount: item?.qty * item.price || 0,
          selected: false,
          isTreatment: false,
          isPackage: false,
          isDentalTreatment: true,
          refundAmount: "",
        };
      });
      setTreatmentItems(initialItems);
    }

    if (refundMethod === "cash") {
      setAccount(data?.relatedBranch);
      setFilteredAccount(filterCash);
    } else {
      setFilteredAccount(filterBank);
    }
  }, [data, refundMethod]);

  useEffect(() => {
    const cashBackAmount =
      totalRefundAmount > calculateNetTotal()
        ? totalRefundAmount - calculateNetTotal()
        : 0;

    const refundPaidAmount =
      calculateNetTotal() > totalRefundAmount
        ? calculateNetTotal() - totalRefundAmount
        : 0;
    setCashBackAmount(cashBackAmount);
    setRefundPaidAmount(refundPaidAmount);
  }, [totalRefundAmount, calculateNetTotal]);

  if (loading) {
    return (
      <div>
        <div className="ml-28 mt-3">
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading refund voucher details...</p>
          </div>
        </div>
      </div>
    );
  }

  const isDisabled =
    data?.isRequested === true ||
    // data?.isAppointmentGenerated === true ||
    data?.isRefundable === false;

  return (
    <div>
      <div className=" mt-3 outlet-container">
        <div className="ml-28">
          <div className="breadcrumb-with-branch">
            <Breadcrumb
              currentPage="Refund Voucher"
              links={[{ title: "Voucher", path: "/voucher" }]}
            />

            {/* Branch Name Display */}
            {data?.createdBy && (
              <Box
                sx={{
                  background: "#ec4899",
                  color: "white",
                  padding: "8px 16px",
                  borderRadius: "8px",
                  marginLeft: "16px",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  animation: "slideIn 0.5s ease-out",
                  boxShadow: "0 2px 12px rgba(236, 72, 153, 0.3)",
                }}
              >
                <Typography
                  variant="body1"
                  component="span"
                  sx={{
                    fontWeight: 600,
                    fontSize: "14px",
                    letterSpacing: "0.3px",
                    textTransform: "uppercase",
                  }}
                >
                  {data?.createdBy?.branchName} Branch
                </Typography>
              </Box>
            )}
          </div>
        </div>

        <div className="refund-voucher-detail-page">
          {/* Approval Section */}
          <ApprovalActions voucher={data} />

          <div className="summaryGrid">
            <div className="summaryCard">
              <div className="summaryTitle">Total Amount</div>
              <div className="summaryValue">
                {" "}
                {data?.totalAmount.toLocaleString()} MMK
              </div>
            </div>

            <div className="summaryCard">
              <div className="summaryTitle">Paid Amount</div>
              <div className="summaryValue summaryGreen">
                {" "}
                {data?.totalPaidAmount.toLocaleString()} MMK
              </div>
            </div>

            <div className="summaryCard">
              <div className="summaryTitle">Refunded</div>
              <div className="summaryValue">
                {data?.refundAmount.toLocaleString()} MMK
              </div>
            </div>

            {/* <div className="summaryCard">
              <div className="summaryTitle">Balance Due</div>
              <div className="summaryValue summaryRed">5000 MMK</div>
            </div> */}
          </div>
          {/* Complete Voucher Information */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  borderRadius: 2,
                  height: "100%",
                }}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    component="div"
                    sx={{
                      marginBottom: 3,
                      fontWeight: 600,
                      color: "#333",
                    }}
                  >
                    Original Voucher Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ marginBottom: 0.5 }}
                      >
                        Voucher Code
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {data?.code}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ marginBottom: 0.5 }}
                      >
                        Voucher Date
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {formatDate(data?.createdAt)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ marginBottom: 0.5 }}
                      >
                        Total Amount
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {data?.totalAmount.toLocaleString()} MMK
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ marginBottom: 0.5 }}
                      >
                        Paid Amount
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 500, color: "#12b76a" }}
                      >
                        {data?.totalPaidAmount.toLocaleString()} MMK
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ marginBottom: 0.5 }}
                      >
                        Payment Method
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {data?.paymentType}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  borderRadius: 2,
                  height: "100%",
                }}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    component="div"
                    sx={{
                      marginBottom: 3,
                      fontWeight: 600,
                      color: "#333",
                    }}
                  >
                    Patient Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ marginBottom: 0.5 }}
                      >
                        Full Name
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {data?.relatedPatient.name}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ marginBottom: 0.5 }}
                      >
                        Phone
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {data?.relatedPatient.phone}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={12}>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ marginBottom: 0.5 }}
                      >
                        Address
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {data?.relatedPatient.address}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Refund Type and Date */}
          <Card
            sx={{
              marginTop: 3,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              borderRadius: 2,
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                component="div"
                sx={{
                  marginBottom: 3,
                  fontWeight: 600,
                  color: "#333",
                }}
              >
                Refund Information
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl>
                    <FormLabel
                      sx={{
                        color: "#333",
                        fontWeight: 500,
                      }}
                    >
                      Refund Type
                    </FormLabel>
                    <RadioGroup
                      row
                      value={refundType}
                      onChange={(e) => setRefundType(e.target.value)}
                    >
                      <FormControlLabel
                        value="CashBack"
                        control={<Radio />}
                        label="Refund by Cash"
                        disabled={isDisabled}
                      />
                      <FormControlLabel
                        value="Treatment"
                        control={<Radio />}
                        label="Refund by Treatment"
                        disabled={isDisabled}
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Refund Date"
                    type="date"
                    value={refundDate}
                    onChange={(e) => setRefundDate(e.target.value)}
                    fullWidth
                    size="small"
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Treatment Items */}
          <div className="card">
            <div className="cardHeader">
              Treatment Items from Original Voucher
            </div>

            <TableContainer
              component={Paper}
              sx={{
                marginTop: 2,
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                borderRadius: 2,
              }}
            >
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={isIndeterminate}
                        checked={isAllSelected}
                        onChange={handleSelectAll}
                        size="small"
                        disabled={isDisabled}
                        sx={{
                          color: "#1976d2",
                          "& .MuiSvgIcon-root": {
                            fontSize: "1.2rem",
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, typography: "body1" }}>
                      No
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, typography: "body1" }}>
                      Treatment
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>
                      qty
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      Unit Price
                    </TableCell>
                    {/* <TableCell align="right" sx={{ fontWeight: 600 }}>
                      Discount
                    </TableCell> */}
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      Refundable
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      Refund Amount
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {treatmentItems.map((item, index) => (
                    <TableRow
                      key={item._id}
                      sx={{
                        "&:hover": { backgroundColor: "#f9f9f9" },
                        backgroundColor: item.selected ? "#e3f2fd" : "inherit",
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={item.selected}
                          onChange={() => handleItemSelect(item._id)}
                          size="small"
                          disabled={isDisabled}
                          sx={{
                            color: "#1976d2",
                            "& .MuiSvgIcon-root": {
                              fontSize: "1.2rem",
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ typography: "body1" }}>
                        {index + 1}
                      </TableCell>
                      <TableCell sx={{ typography: "body1" }}>
                        {item.name}
                      </TableCell>
                      <TableCell align="center">{item.qty}</TableCell>
                      <TableCell align="right">
                        {item.unitPrice.toLocaleString()} MMK
                      </TableCell>

                      <TableCell align="right">
                        <Chip
                          label={`${item.totalAmount.toLocaleString()} MMK`}
                          color="success"
                          variant="outlined"
                          size="small"
                          sx={{ fontFamily: "monospace" }}
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ width: 150 }}>
                        <TextField
                          value={item.refundAmount}
                          onChange={(e) =>
                            handleRefundAmountChange(item._id, e.target.value)
                          }
                          disabled={
                            !item.selected ||
                            data?.isRequested === true 
                            // data?.isAppointmentGenerated === true
                          }
                          placeholder="0"
                          size="small"
                          type="number"
                          inputProps={{
                            min: 0,
                            max: item.totalAmount,
                            style: { textAlign: "right" },
                          }}
                          sx={{
                            "& .MuiInputBase-input.Mui-disabled": {
                              backgroundColor: "#f5f5f5",
                              borderRadius: 1,
                            },
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow
                    sx={{ backgroundColor: "#f5f5f5", fontWeight: 600 }}
                  >
                    <TableCell colSpan={6} align="right">
                      <Typography variant="subtitle1" fontWeight={600}>
                        Total Refund Amount:
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="subtitle1"
                        fontWeight={600}
                        color="primary"
                        sx={{ fontFamily: "monospace" }}
                      >
                        {totalRefundAmount.toLocaleString()} MMK
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            {selectedCount > 0 && (
              <Box sx={{ marginTop: 2, textAlign: "center" }}>
                <Typography variant="body2" color="textSecondary">
                  {selectedCount} item{selectedCount > 1 ? "s" : ""} selected
                  for refund
                </Typography>
              </Box>
            )}
          </div>

          {refundType === "Treatment" && (
            <Card
              sx={{
                marginTop: 3,

                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                borderRadius: 2,
                overflow: "visible",
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  component="div"
                  sx={{
                    marginBottom: 3,
                    fontWeight: 600,
                    color: "#333",
                  }}
                >
                  Replacement Section
                </Typography>

                <Typography
                  variant="p"
                  component="div"
                  sx={{
                    marginBottom: 3,
                    fontWeight: 600,
                    color: "#999",
                  }}
                >
                  Sale Type
                </Typography>
                <RadioGroup row value="Treatments">
                  <FormControlLabel
                    value="Treatments"
                    control={<Radio />}
                    label="Treatments"
                  />
                </RadioGroup>

                <Divider sx={{ marginY: 1 }} />

                {/* Treatment Unit Select Box */}
                <Typography
                  variant="p"
                  component="div"
                  sx={{
                    marginBottom: 2,
                    fontWeight: 600,
                    color: "#333",
                  }}
                >
                  Select Treatment Unit
                </Typography>

                <ReactSelect
                  value={
                    selectedTreatment
                      ? treatmentsData?.data?.find(
                          (t) => t._id === selectedTreatment,
                        )
                      : null
                  }
                  onChange={(selectedOption) =>
                    handleTreatmentSelect(selectedOption?._id)
                  }
                  options={treatmentsData?.data || []}
                  getOptionLabel={(option) => option.name}
                  getOptionValue={(option) => option._id}
                  placeholder="Select Treatment Unit"
                  isLoading={treatmentsLoading}
                  isClearable
                  isSearchable
                  styles={{
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      borderColor: state.isFocused ? "#1976d2" : "#ccc",
                      boxShadow: state.isFocused ? "0 0 0 1px #1976d2" : "none",
                      "&:hover": {
                        borderColor: "#1976d2",
                      },
                    }),
                    option: (baseStyles, state) => ({
                      ...baseStyles,
                      backgroundColor: state.isFocused ? "#f3f4f6" : "white",
                      color: "#333",
                      padding: "12px 16px",
                    }),
                    menu: (baseStyles) => ({
                      ...baseStyles,
                      zIndex: 9999,
                      maxHeight: "200px",
                      boxShadow:
                        "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                    }),
                    menuList: (baseStyles) => ({
                      ...baseStyles,
                      maxHeight: "200px",
                      overflowY: "auto",
                    }),
                  }}
                />

                {/* Voucher Design Table */}
                {selectedTreatment && (
                  <Box>
                    <Typography
                      variant="h6"
                      component="div"
                      sx={{
                        marginBottom: 2,
                        fontWeight: 600,
                        color: "#374151",
                      }}
                    >
                      Replace Treatment List
                    </Typography>

                    <TableContainer
                      component={Paper}
                      sx={{
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        borderRadius: 2,
                        border: "1px solid #e4e7ec",
                      }}
                    >
                      <Table>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                color: "#374151",
                                typography: "body1",
                              }}
                            >
                              #
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                color: "#374151",
                                typography: "body1",
                              }}
                            >
                              Item Name
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{ fontWeight: 600, color: "#374151" }}
                            >
                              Qty
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ fontWeight: 600, color: "#374151" }}
                            >
                              Price
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ fontWeight: 600, color: "#374151" }}
                            >
                              Total
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{ fontWeight: 600, color: "#374151" }}
                            >
                              Action
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {voucherItems.map((item, index) => (
                            <TableRow
                              key={item._id}
                              sx={{
                                "&:hover": { backgroundColor: "#f9fafb" },
                              }}
                            >
                              <TableCell
                                sx={{ typography: "body1", color: "#374151" }}
                              >
                                {index + 1}
                              </TableCell>
                              <TableCell
                                sx={{
                                  typography: "body1",
                                  color: "#374151",
                                  fontWeight: 500,
                                }}
                              >
                                {item.name}
                              </TableCell>
                              <TableCell align="center">{item.qty}</TableCell>
                              <TableCell
                                align="right"
                                sx={{
                                  color: "#374151",
                                  fontFamily: "monospace",
                                }}
                              >
                                {(item.price || 0).toLocaleString()} MMK
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{
                                  color: "#374151",
                                  fontFamily: "monospace",
                                  fontWeight: 600,
                                }}
                              >
                                {(item.total || 0).toLocaleString()} MMK
                              </TableCell>
                              <TableCell align="center">
                                <IconButton
                                  onClick={() => handleRemoveItem(item._id)}
                                  sx={{
                                    color: "#ec4899",
                                    "&:hover": {
                                      backgroundColor: "#fce7f3",
                                      color: "#be185d",
                                    },
                                  }}
                                >
                                  <RemoveCircleOutline />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                          {voucherItems.length === 0 && (
                            <TableRow>
                              <TableCell
                                colSpan={6}
                                align="center"
                                sx={{ py: 4, color: "#6b7280" }}
                              >
                                <Typography
                                  variant="body2"
                                  color="textSecondary"
                                >
                                  No items added. Select a treatment to begin.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                          {voucherItems.length > 0 && (
                            <>
                              <TableRow
                                sx={{
                                  backgroundColor: "#f8fafc",
                                  fontWeight: 600,
                                }}
                              >
                                <TableCell colSpan={4} align="right">
                                  <Typography
                                    variant="subtitle1"
                                    fontWeight={600}
                                    color="#374151"
                                  >
                                    Replacement Total:
                                  </Typography>
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{
                                    color: "#374151",
                                    fontFamily: "monospace",
                                    fontWeight: 600,
                                  }}
                                >
                                  {calculateReplacementTotal().toLocaleString()}{" "}
                                  MMK
                                </TableCell>
                                <TableCell></TableCell>
                              </TableRow>
                              <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                                <TableCell colSpan={4} align="right">
                                  <Typography variant="body2" color="#6b7280">
                                    Discount:
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <TextField
                                    value={replacementDiscount}
                                    onChange={(e) =>
                                      setReplacementDiscount(
                                        parseFloat(e.target.value) || 0,
                                      )
                                    }
                                    type="number"
                                    size="small"
                                    inputProps={{
                                      min: 0,
                                      style: { textAlign: "right" },
                                    }}
                                    sx={{
                                      "& .MuiOutlinedInput-root": {
                                        "& fieldset": {
                                          borderColor: "#d1d5db",
                                        },
                                        "&:hover fieldset": {
                                          borderColor: "#9ca3af",
                                        },
                                        "&.Mui-focused fieldset": {
                                          borderColor: "#6b7280",
                                        },
                                      },
                                    }}
                                  />
                                </TableCell>
                                <TableCell></TableCell>
                              </TableRow>
                              <TableRow
                                sx={{
                                  backgroundColor: "#f1f5f9",
                                  fontWeight: 700,
                                }}
                              >
                                <TableCell colSpan={4} align="right">
                                  <Typography
                                    variant="subtitle1"
                                    fontWeight={700}
                                    color="#111827"
                                  >
                                    Replacement Net Total:
                                  </Typography>
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{
                                    color: "#111827",
                                    fontFamily: "monospace",
                                    fontWeight: 700,
                                  }}
                                >
                                  {calculateNetTotal().toLocaleString()} MMK
                                </TableCell>
                                <TableCell></TableCell>
                              </TableRow>
                            </>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {/* Refund Reason */}
          <Card
            sx={{
              marginTop: 3,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              borderRadius: 2,
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                component="div"
                sx={{
                  marginBottom: 3,
                  fontWeight: 600,
                  color: "#333",
                }}
              >
                Refund Details
              </Typography>

              <Grid container spacing={3}>
                {cashBackAmount > 0 && refundType === "Treatment" && (
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <TextField
                        id="cashback-amount"
                        label="Cashback Amount"
                        type="number"
                        fullWidth
                        value={cashBackAmount}
                        onChange={(e) => setCashBackAmount(e.target.value)}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="start">
                              MMK
                            </InputAdornment>
                          ),
                        }}
                      />
                    </FormControl>
                  </Grid>
                )}

                {refundPaidAmount > 0 && refundType === "Treatment" && (
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <TextField
                        id="refund-paid-amount"
                        label="Refund Paid Amount"
                        type="number"
                        fullWidth
                        value={refundPaidAmount}
                        onChange={(e) => setRefundPaidAmount(e.target.value)}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="start">
                              MMK
                            </InputAdornment>
                          ),
                        }}
                      />
                    </FormControl>
                  </Grid>
                )}

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="refund-method-label">
                      Refund Method
                    </InputLabel>
                    <Select
                      labelId="refund-method-label"
                      id="refund-method"
                      value={refundMethod}
                      label="Refund Method"
                      onChange={(e) => setRefundMethod(e.target.value)}
                    >
                      <MenuItem value="cash">Cash</MenuItem>
                      <MenuItem value="bank">Bank</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel htmlFor="account-label">Account</InputLabel>
                    <Select
                      id="account"
                      value={account}
                      label="Account"
                      onChange={handleAccountChange}
                    >
                      {filteredAccounts?.map((account) => (
                        <MenuItem key={account._id} value={account._id}>
                          {account.subHeader}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Box sx={{ marginTop: 3 }}>
                <TextField
                  id="remark"
                  label="Remark"
                  multiline
                  rows={4}
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                  fullWidth
                  variant="outlined"
                  placeholder="Enter refund reason or additional notes..."
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&:hover fieldset": {
                        borderColor: "#1976d2",
                      },
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>

          {/* Related Vouchers */}
          {/* <RelatedVouchers
            currentVoucher={voucher}
            relatedVouchers={relatedVouchers}
          /> */}

          <div className="approval-actions">
            <button className="btn-cancel">Cancel</button>
            <button
              className={`btn-approve ${selectedCount === 0 && "btn-disabled"}`}
              onClick={handleApprove}
              disabled={selectedCount === 0}
            >
              <CheckCircle2 className="btn-icon" />
              Approve Refund - {totalRefundAmount.toLocaleString()} MMK
            </button>
          </div>

          <Dialog
            open={approveModal.open}
            onClose={closeApproveModal}
            aria-labelledby="approve-dialog-title"
            aria-describedby="approve-dialog-description"
          >
            <DialogTitle id="approve-dialog-title">
              Confirm Refund Approval
            </DialogTitle>
            <DialogTitle>
              <DialogContentText id="approve-dialog-description">
                Are you sure you want to approve this refund request?
              </DialogContentText>
            </DialogTitle>
            <DialogActions>
              <Button
                onClick={closeApproveModal}
                color="secondary"
                disabled={postLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={confirmApprove}
                variant="contained"
                sx={{
                  backgroundColor: "#4caf50",
                  "&:hover": { backgroundColor: "#45a049" },
                }}
                autoFocus
                disabled={postLoading}
              >
                {postLoading ? (
                  <CircularProgress size={20} sx={{ color: "white" }} />
                ) : (
                  "Approve"
                )}
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      </div>
    </div>
  );
};

export default ReFundVoucherDetail;
